import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useChartColors } from './useChartColors';

interface BarDef {
  dataKey: string;
  name?: string;
  color?: string;
  stackId?: string;
}

interface BaseBarChartProps {
  data: Record<string, unknown>[];
  bars: BarDef[];
  xAxisKey?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  showYAxis?: boolean;
  layout?: 'horizontal' | 'vertical';
}

export default function BaseBarChart({
  data,
  bars,
  xAxisKey = 'name',
  height = 250,
  showGrid = false,
  showLegend = false,
  showYAxis = false,
  layout = 'vertical',
}: BaseBarChartProps) {
  const colors = useChartColors();

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={layout} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />}
        {layout === 'vertical' ? (
          <>
            <XAxis dataKey={xAxisKey} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            {showYAxis && <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />}
          </>
        ) : (
          <>
            <YAxis dataKey={xAxisKey} type="category" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
            <XAxis type="number" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
          </>
        )}
        <Tooltip
          contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }}
          labelStyle={{ color: 'var(--color-text-primary)' }}
          itemStyle={{ color: 'var(--color-text-secondary)' }}
        />
        {showLegend && <Legend wrapperStyle={{ fontSize: 11, color: 'var(--color-text-muted)' }} />}
        {bars.map((bar, i) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name || bar.dataKey}
            fill={bar.color || colors[i % colors.length]}
            stackId={bar.stackId}
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
